import { Avatar, AvatarFallback, AvatarImage, cn, SimpleTooltip } from '@opengoal/ui';
import { useTranslation } from 'react-i18next';
import {
  TaskPriority,
  TaskStatusCategory,
  type TaskLabelRefDto,
  type TaskMemberRefDto,
  type TaskTeamRefDto,
} from '@/api/generated/model';
import { initials } from '@/shared/lib/format';

/**
 * The small visual vocabulary of tasks, shared by the board, the list, the
 * modal, the filter bar and Settings.
 *
 * Status, label and team colours are data the agency chose in Settings, not
 * theme tokens, so they are applied as inline styles — the one place the
 * "semantic classes only" rule cannot apply.
 */

export function StatusIcon({
  category,
  color,
  className,
}: {
  category: TaskStatusCategory;
  color: string;
  className?: string;
}) {
  const common = {
    viewBox: '0 0 14 14',
    className: cn('size-3.5 shrink-0', className),
    style: { color },
    'aria-hidden': true,
  } as const;

  switch (category) {
    case TaskStatusCategory.backlog:
      return (
        <svg {...common}>
          <circle
            cx="7"
            cy="7"
            r="5.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeDasharray="2 1.6"
          />
        </svg>
      );
    case TaskStatusCategory.unstarted:
      return (
        <svg {...common}>
          <circle cx="7" cy="7" r="5.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      );
    case TaskStatusCategory.started:
      return (
        <svg {...common}>
          <circle cx="7" cy="7" r="5.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <path d="M7 3.5 A3.5 3.5 0 0 1 7 10.5 Z" fill="currentColor" />
        </svg>
      );
    case TaskStatusCategory.completed:
      return (
        <svg {...common}>
          <circle cx="7" cy="7" r="6.25" fill="currentColor" />
          <path
            d="M4.5 7.2 6.3 9 9.6 5.3"
            fill="none"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case TaskStatusCategory.canceled:
      return (
        <svg {...common}>
          <circle cx="7" cy="7" r="6.25" fill="currentColor" />
          <path
            d="M5 5 9 9M9 5 5 9"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      );
  }
}

/** Linear's signal bars: one per level, the urgent one a filled warning square. */
export function PriorityIcon({
  priority,
  className,
}: {
  priority: TaskPriority;
  className?: string;
}) {
  const classes = cn('size-3.5 shrink-0', className);
  if (priority === TaskPriority.urgent) {
    return (
      <svg viewBox="0 0 14 14" className={cn(classes, 'text-destructive')} aria-hidden>
        <rect x="1" y="1" width="12" height="12" rx="3" fill="currentColor" />
        <path d="M7 3.8v4" stroke="white" strokeWidth="1.6" strokeLinecap="round" />
        <circle cx="7" cy="10.2" r="0.9" fill="white" />
      </svg>
    );
  }
  if (priority === TaskPriority.none) {
    return (
      <svg viewBox="0 0 14 14" className={cn(classes, 'text-muted-foreground')} aria-hidden>
        {[2, 6, 10].map((x) => (
          <rect key={x} x={x} y="6.25" width="2" height="1.5" rx="0.5" fill="currentColor" />
        ))}
      </svg>
    );
  }
  const filled = priority === TaskPriority.high ? 3 : priority === TaskPriority.medium ? 2 : 1;
  return (
    <svg viewBox="0 0 14 14" className={cn(classes, 'text-foreground')} aria-hidden>
      {[0, 1, 2].map((bar) => (
        <rect
          key={bar}
          x={1.5 + bar * 4}
          y={9 - bar * 3}
          width="3"
          height={3 + bar * 3}
          rx="0.75"
          fill="currentColor"
          opacity={bar < filled ? 1 : 0.25}
        />
      ))}
    </svg>
  );
}

/** Priority as the mock shows it on cards: a compact pill. */
export function PriorityBadge({ priority }: { priority: TaskPriority }) {
  const { t } = useTranslation();
  if (priority === TaskPriority.none) return null;
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] font-semibold',
        priority === TaskPriority.urgent || priority === TaskPriority.high
          ? 'border-transparent bg-destructive text-destructive-foreground'
          : priority === TaskPriority.medium
            ? 'border-transparent bg-foreground text-background'
            : 'border-border bg-background text-foreground',
      )}
    >
      {t(`tasks.priorityShort.${priority}`)}
    </span>
  );
}

export function MemberAvatar({
  member,
  className,
}: {
  member: Pick<TaskMemberRefDto, 'name' | 'image'>;
  className?: string;
}) {
  return (
    <Avatar className={cn('size-5 text-[9px]', className)}>
      {member.image ? <AvatarImage src={member.image} alt="" /> : null}
      <AvatarFallback className="text-[9px] font-semibold">{initials(member.name)}</AvatarFallback>
    </Avatar>
  );
}

/** Overlapping avatars, capped so a task given to ten people stays one line. */
export function AvatarStack({ members, max = 3 }: { members: TaskMemberRefDto[]; max?: number }) {
  if (members.length === 0) return null;
  const shown = members.slice(0, max);
  const rest = members.length - shown.length;
  return (
    <SimpleTooltip content={members.map((member) => member.name).join(', ')}>
      <span className="flex items-center -space-x-1.5 rtl:space-x-reverse">
        {shown.map((member) => (
          <MemberAvatar key={member.id} member={member} className="ring-2 ring-card" />
        ))}
        {rest > 0 ? (
          <span className="ms-1.5 text-[10px] font-medium text-muted-foreground">+{rest}</span>
        ) : null}
      </span>
    </SimpleTooltip>
  );
}

export function ColorDot({ color, className }: { color: string; className?: string }) {
  return (
    <span
      className={cn('inline-block size-2 shrink-0 rounded-full', className)}
      style={{ backgroundColor: color }}
      aria-hidden
    />
  );
}

export function LabelChip({ label }: { label: TaskLabelRefDto }) {
  return (
    <span className="inline-flex max-w-32 items-center gap-1 rounded-full border border-border px-1.5 py-px text-[10px] font-medium text-muted-foreground">
      <ColorDot color={label.color} className="size-1.5" />
      <span className="truncate">{label.name}</span>
    </span>
  );
}

export function TeamChip({ team }: { team: TaskTeamRefDto }) {
  return (
    <span className="inline-flex max-w-36 items-center gap-1 text-[11px] font-medium text-muted-foreground">
      <span
        className="grid size-4 shrink-0 place-items-center rounded text-[9px] font-bold text-white"
        style={{ backgroundColor: team.color }}
        aria-hidden
      >
        {team.name.slice(0, 1).toUpperCase()}
      </span>
      <span className="truncate">{team.name}</span>
    </span>
  );
}
